import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  ViewChildren,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  findActionByNameAndValue,
  findConditionByNameAndValue,
  RulesOperator,
  RulesActions,
  RulesConditions,
} from '@app/features/automation-rules/utils';
import { AutomationRulesActions } from '../../../actions';
import { SelectedRuleParams } from '../rule-template-selector.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RuleTemplateService } from '../rule-template.service';
import { MatSelect } from '@angular/material/select';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';

@Component({
  selector: 'order-fulfilled-template',
  templateUrl: './order-fulfilled-template.component.html',
  styleUrls: ['./order-fulfilled-template.component.scss'],
  standalone: false,
})
export class OrderFulfilledTemplateComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() conditions: Array<any>;
  @Input() actions: Array<any>;
  @Input() selectedRuleParams?: SelectedRuleParams;

  @Input() onChangeConditionSelect: EventEmitter<any>;
  @Input() onChangeActionSelect: EventEmitter<any>;
  @Input() onChangeOperatorSelect: EventEmitter<any>;
  @Input() onChangeOperator2Select: EventEmitter<any>;
  @Input() onRemoveCondition: EventEmitter<any>;
  @ViewChildren('conditionSelect') conditionSelects;
  @ViewChildren('selectList') selectList;

  destroyed$ = new Subject();
  headsUp: string[] = [];

  fulfillmentStatusOptions: DropdownOption[];

  // ---Conditions---
  currentOperatorValue: RulesOperator[][] = [[null]];
  currentOperator2Value: RulesOperator[][] = [[null]];
  currentAllConditionsSelectValue: AutomationRulesActions.SelectedCondition[][] = [[null]];
  currentFulfillmentStatusValue: DropdownOption[][] = [[null]];

  // ---Action---
  currentActionSelectValue: AutomationRulesActions.SelectedAction;
  currentActionAddTagsValue: string[] = [];
  currentActionDeleteTagsValue: string[] = [];

  indexOfLastMatOption: any;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private ruleTemplateService: RuleTemplateService,
  ) {}

  ngOnInit() {
    this.initListeners();
    this.initDropdownOptions();
    this.initSelectedRuleParams();
  }

  ngAfterViewInit() {
    this.selectList.changes.subscribe(() => {
      this.conditionSelects.forEach((condition: MatSelect) => {
        if (!this.indexOfLastMatOption) {
          return;
        }
        const [j, i] = this.indexOfLastMatOption;
        const atrb = condition._elementRef.nativeElement.getAttribute('data-2dIndex');
        if (condition._elementRef.nativeElement.getAttribute('data-2dIndex') == j + ',' + i) {
          this.indexOfLastMatOption = null;
          condition.open();
          this.changeDetectorRef.detectChanges();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  initListeners() {
    this.onChangeActionSelect.pipe(takeUntil(this.destroyed$)).subscribe((e) => this.setHeadsUpNotifications(e));
  }

  initDropdownOptions() {
    this.fulfillmentStatusOptions = this.ruleTemplateService.convertValuesToDropDownOptions({
      conditions: this.conditions[0].values,
      conditionName: RulesConditions.OrderFulfillmentStatus,
      labelField: 'label',
      valueField: 'value',
    });
  }

  initSelectedRuleParams() {
    this.currentAllConditionsSelectValue = this.getSelectedConditions();
    this.currentActionSelectValue = this.getSelectedAction();

    if (this.currentActionSelectValue) {
      const selectedAction = { ...this.currentActionSelectValue };
      if (selectedAction.name === RulesActions.AddTags) {
        this.tagsChanged(this.currentActionAddTagsValue, 'add');
      } else if (selectedAction.name === RulesActions.DeleteTags) {
        this.tagsChanged(this.currentActionDeleteTagsValue, 'delete');
      } else {
        this.onChangeActionSelect.emit(selectedAction);
      }
    }
  }

  getSelectedConditions() {
    if (!this.selectedRuleParams) {
      return [[null]];
    }

    const selectedConditions = [[]];
    const orWhenLength = this.selectedRuleParams.orWhen.length;

    for (let j = 0; j < orWhenLength; j++) {
      this.addOrCondition(j, false);
      for (let i = 0; i < this.selectedRuleParams.orWhen[j].andWhen.length; i++) {
        this.addCondition([j, i], false);
        const andWhenName: string = Object.keys(this.selectedRuleParams.orWhen[j].andWhen[i])?.pop();
        const andWhenValue: any = Object.values(this.selectedRuleParams.orWhen[j].andWhen[i])?.pop();
        const andWhenValueToCheck = andWhenValue.values ? andWhenValue.values : andWhenValue;
        const condition = this.conditions[0].values.find(
          findConditionByNameAndValue.bind(null, andWhenName, andWhenValueToCheck),
        );

        if (!selectedConditions[j]) {
          selectedConditions[j] = [];
        }
        selectedConditions[j].push(condition);
        switch (andWhenName) {
          case RulesConditions.OrderFulfillmentStatus:
            this.currentFulfillmentStatusValue[j][i] = andWhenValue.fulfillmentStatus;
            break;
        }
      }
    }

    return selectedConditions;
  }

  changeConditionSelect({ value: selectedCondition }, indices: [number, number]): void {
    const [j, i] = indices;
    const updatedSelectedCondition = { ...selectedCondition };

    switch (selectedCondition.name) {
      case RulesConditions.OrderFulfillmentStatus:
        const fulfillmentStatus = this.fulfillmentStatusOptions[0];
        this.currentFulfillmentStatusValue[j][i] = fulfillmentStatus;
        updatedSelectedCondition.value = { fulfillmentStatus };
        break;
    }

    selectedCondition = updatedSelectedCondition;
    this.onChangeConditionSelect.emit({ value: selectedCondition, index: [j, i] });
    this.onResetActionSelectValue();
  }

  changeOperatorSelect(event, indices: [number, number]) {
    const [j, i] = indices;
    let operator;
    let is;

    if (event?.source?.controlType == 'mat-select') {
      if (event.value == 'is') {
        operator = { value: 'is', label: 'is' };
        is = true;
      }
      if (event.value == 'is-not') {
        operator = { value: 'is-not', label: 'is not' };
        is = false;
      }
    } else {
      operator = event;
    }

    this.currentOperatorValue[j][i] = operator;
    this.onChangeOperatorSelect.emit({ operator, is, index: [j, i] });
  }

  changeOperator2Select(operator, indices: [number, number]) {
    const [j, i] = indices;
    this.currentOperator2Value[j][i] = operator;
    this.onChangeOperator2Select.emit({ operator, index: [j, i] });
  }

  getSelectedAction() {
    if (!this.selectedRuleParams) {
      return null;
    }
    const selectedActionName = Object.keys(this.selectedRuleParams.andThen[0])[0];
    const selectedActionValue: any = Object.values(this.selectedRuleParams.andThen[0])[0];
    const actionData = this.actions[0]?.values?.find((a) => a.name === selectedActionName);

    switch (selectedActionName) {
      case RulesActions.AddTags:
        this.currentActionAddTagsValue = selectedActionValue;
        break;
      case RulesActions.DeleteTags:
        this.currentActionDeleteTagsValue = selectedActionValue;
        break;
    }
    return this.actions[0].values.find(findActionByNameAndValue.bind(null, selectedActionName, selectedActionValue));
  }

  changeActionSelect({ value: selectedAction }): void {
    switch (selectedAction.name) {
      default:
        // do not set for multiple values
        if (!selectedAction.values) {
          this.onChangeActionSelect.emit(selectedAction);
        }
    }
  }

  getFormattedText(text: string) {
    return text.replace(/\[\[/g, '<span class="highlight">').replace(/\]\]/g, '</span>');
  }

  getActionText(action: AutomationRulesActions.SelectedAction) {
    const text = `<span class="value">${action.text}</span>`;
    return text;
  }

  onResetActionSelectValue() {
    this.currentActionSelectValue = null;
    this.currentActionAddTagsValue = [];
    this.currentActionDeleteTagsValue = [];
    this.onChangeActionSelect.emit(this.currentActionSelectValue);
  }

  setHeadsUpNotifications(action: any) {
    this.headsUp = this.ruleTemplateService.setHeadsUpNotifications(action, this.currentActionSelectValue);
  }

  addOrCondition(index, shouldAddChildCondition = true): void {
    this.currentAllConditionsSelectValue.push([]);
    this.currentOperatorValue.push([]);
    this.currentOperator2Value.push([]);

    this.currentFulfillmentStatusValue.push([]);
    if (shouldAddChildCondition) {
      this.addCondition([index, 0]);
    }
  }

  addCondition(indices: [number, number], shouldEmit = true): void {
    const [j, i] = indices;
    this.currentAllConditionsSelectValue[j].push(null);
    this.currentOperatorValue[j].push(null);
    this.currentFulfillmentStatusValue[j].push(null);
    if (shouldEmit) {
      this.indexOfLastMatOption = indices;
      this.onChangeConditionSelect.emit({ value: {}, index: [j, i] });
    }
  }

  checkIfAllValuesInserted() {
    const isAllInserted = this.currentAllConditionsSelectValue?.every((conditionGroup, groupIndex) => {
      return conditionGroup.every((currentConditionSelectValue, index) => {
        return this.checkIfAllValuesInsertedForCurrentCondition(currentConditionSelectValue, groupIndex, index);
      });
    });
    return isAllInserted;
  }

  checkIfAllValuesInsertedForConditionGroup(row) {
    const isAllInserted = this.currentAllConditionsSelectValue[row]?.every((currentConditionSelectValue, index) => {
      return this.checkIfAllValuesInsertedForCurrentCondition(currentConditionSelectValue, row, index);
    });
    return isAllInserted;
  }

  checkIfAllValuesInsertedForCurrentCondition(currentConditionSelectValue, groupIndex, index) {
    switch (currentConditionSelectValue?.name) {
      case RulesConditions.OrderFulfillmentStatus:
        return !!this.currentFulfillmentStatusValue[groupIndex][index];
    }
    return currentConditionSelectValue?.value;
  }

  removeCondition(indices: [number, number]) {
    const [j, i] = indices;
    this.currentAllConditionsSelectValue[j].splice(i, 1);
    this.currentOperatorValue[j].splice(i, 1);
    this.currentFulfillmentStatusValue[j].splice(i, 1);
    if (j > 0 && i == 0 && this.currentAllConditionsSelectValue[j].length == 0) {
      this.currentAllConditionsSelectValue.splice(j, 1);
      this.currentOperatorValue.splice(j, 1);
      this.currentFulfillmentStatusValue.splice(j, 1);
    }

    this.onRemoveCondition.emit({ index: [j, i] });
  }

  isActionDisabled(currentAction, actionIndex) {}

  onOrderFulfillmentStatusConditionChanged(value, indices: [number, number]) {
    const [j, i] = indices;
    const fulfillmentStatusCondition = this.ruleTemplateService.changeFulfillmentStatusSelect(
      value,
      i,
      this.currentAllConditionsSelectValue[j],
    );
    this.onChangeConditionSelect.emit({ value: fulfillmentStatusCondition, index: [j, i] });
  }

  tagsChanged(tags: string[], action: 'add' | 'delete') {
    const tagsAction = {
      ...this.currentActionSelectValue,
    };
    switch (action) {
      case 'add':
        this.currentActionAddTagsValue = tags;
        break;
      case 'delete':
        this.currentActionDeleteTagsValue = tags;
        break;
    }

    tagsAction.value = tags;
    this.onChangeActionSelect.emit(tagsAction);
  }
}
